import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import Joyride, { Callback, Step } from 'react-joyride';
import { Trans, useTranslation } from 'react-i18next';

import Button from '@ingka/button';
import cross from '@ingka/ssr-icon/paths/cross';

import { JOYRIDE_STYLES, adapter } from '../common';
import { useTrackInteraction } from '@/lib/hooks/tracking';

const STORAGE_KEY = 'copyImageUrlShown';

const TutorialStep: FC<{
  title?: string;
  description: string;
  onClose: () => void;
}> = ({ title, description, onClose }) => {
  // As this component could be loaded before seting up i18n, we need to pass
  // the "t" function to the "Trans" component to re-render when i18n is ready.
  const { t } = useTranslation();

  return (
    <>
      <div className="flex flex-row justify-end px-4">
        <Button
          type="tertiary"
          onClick={onClose}
          ssrIcon={cross}
          text={t('label.close')}
          iconOnly
        />
      </div>
      <div className="px-[48px]">
        {title && (
          <h1 className="text-2xl font-bold leading-8 mb-[10px]">
            <strong>
              <Trans t={t} i18nKey={title} />
            </strong>
          </h1>
        )}
        <p className="text-xl leading-loose">
          <Trans t={t} i18nKey={description} />
        </p>
      </div>
    </>
  );
};

const getTutorialSteps: (onClose: () => void) => Step[] = (onClose) => [
  {
    content: (
      <TutorialStep
        onClose={onClose}
        title="tutorials.copyImageUrl.step1.title"
        description="tutorials.copyImageUrl.step1.description"
      />
    ),
    placement: 'center',
    target: '.tutorial__copy-image-url--step1',
  },
];

export const CopyImageUrlTutorial: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation();
  const [forceClose, setForceClose] = useState(false);
  const [trackInteraction] = useTrackInteraction();
  const showTutorial = !adapter.getItem(STORAGE_KEY) && !forceClose;
  const handleJoyrideCallback: Callback = (data) => {
    if (['reset', 'skip', 'close'].includes(data.action)) {
      setForceClose(true);
      adapter.setItem(STORAGE_KEY, true);
      // Track events to get user insights
      trackInteraction({
        content: 'copy-image-url-tutorial',
        type: 'click',
        action: data.action === 'reset' ? 'finish' : data.action,
      });
    }
  };
  const handleCloseButtonClick = useCallback(() => {
    handleJoyrideCallback({ action: 'close' } as any);
  }, []);
  const steps = useMemo(() => getTutorialSteps(handleCloseButtonClick), []);

  return (
    <div>
      {showTutorial && (
        <Joyride
          data-testid="joyride"
          continuous
          run={showTutorial}
          scrollToFirstStep
          hideBackButton={true}
          hideCloseButton
          steps={steps}
          styles={{
            ...JOYRIDE_STYLES,
          }}
          locale={{
            last: 'Ok',
          }}
          callback={handleJoyrideCallback}
        />
      )}

      {children}
    </div>
  );
};
